import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import './Profile.css';

export function Profile() {
  const { currentUser, updateDisplayName } = useAuth();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(currentUser?.displayName ?? '');
  const [saving, setSaving] = useState(false);

  if (!currentUser) {
    return null;
  }

  const name = editing ? displayName : (currentUser.displayName || 'User');
  const email = currentUser.email ?? '';

  async function handleSaveName() {
    const trimmed = displayName.trim();
    if (!trimmed) {
      showToast('Name cannot be empty', 'error');
      return;
    }
    if (!currentUser || trimmed === (currentUser.displayName ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateDisplayName(trimmed);
      setEditing(false);
      showToast('Profile updated', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update name', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-page">
      <div className="container">
        <h1>My Profile</h1>

        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              {(currentUser.displayName ?? email)[0].toUpperCase()}
            </div>
            {editing ? (
              <div className="profile-edit-name">
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Display name"
                  autoFocus
                />
                <div className="profile-edit-actions">
                  <button type="button" onClick={handleSaveName} disabled={saving} className="profile-btn profile-btn--primary">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => { setEditing(false); setDisplayName(currentUser.displayName ?? ''); }} disabled={saving} className="profile-btn profile-btn--ghost">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2>{name}</h2>
                <button type="button" onClick={() => { setEditing(true); setDisplayName(currentUser.displayName ?? ''); }} className="profile-edit-btn" aria-label="Edit name">
                  ✏️ Edit name
                </button>
              </>
            )}
            <p className="profile-email">{email}</p>
            <span className={`role-badge ${currentUser.role}`}>{currentUser.role}</span>
          </div>

          <div className="profile-info">
            <div className="info-item">
              <label>User ID</label>
              <p className="profile-uid">{currentUser.uid}</p>
            </div>
            <div className="info-item">
              <label>Account type</label>
              <p className="capitalize">{currentUser.role}</p>
            </div>
          </div>

          <div className="profile-quick-links">
            <h3>Quick links</h3>
            <div className="quick-links-grid">
              <Link to="/orders" className="quick-link">
                📦 Orders
              </Link>
              <Link to="/cart" className="quick-link">
                🛒 Cart
              </Link>
              <Link to="/wishlist" className="quick-link">
                ♥ Wishlist
              </Link>
              {currentUser.role === 'owner' && (
                <Link to="/owner/dashboard" className="quick-link">
                  🏪 My Store
                </Link>
              )}
              {currentUser.role === 'admin' && (
                <Link to="/admin/dashboard" className="quick-link">
                  ⚙️ Admin
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
